import moment from "moment";
import "moment-timezone";
import saleutil from "@dotpe/rista-library/lib/sale/sale_util.js";
import store from "../../redux-store";
// `getBasicSaleData` now lives in the slice that calls it at eval time (see there). Imported as
// well as re-exported because this file USES it below — a re-export creates no binding (hazard 30).
import { getBasicSaleData } from "../../redux-store/slices/SaleSlice";
export { getBasicSaleData };
import { notifyError } from "../../effects-bridge";
import {
  DISCOUNT_MODES,
  DISCOUNT_TYPES,
  EXTERNAL_LOYALTY_PARTNERS,
  SALE_SOURCE_TYPES,
} from "../../data/constants";
import { CREATE_TICKET_ON_SAVE, LOCALSTORAGE_KEYS } from "../../utils/constants";
import { getLocalStorage, removeLocalStorage } from "../../utils/localStorage";
import { updateCurrentTableInfo } from "../../redux-store/slices/TableViewSlice";
import { applyDiscountSuccess } from "../../redux-store/slices/DiscountSlice";
import { unBlockExternalDiscount } from "../../redux-store/slices/Loyalty";
import {
  getActiveProvider,
  isExternalLoyaltyProvider,
  isExternalProviderC1,
  isLoyaltyPointDiscountType,
} from "../loyaltyHelper";
import { checkAndUpdateApprovalState } from "../updateDiscountApprovalState";
import { getChannelDef, resetCustomer } from "./shared";
import { isAdvanceOrder } from "../myOrderHelpers";
import { saveSaleData } from "../../redux-store/slices/SaleSlice";

/**
 * `structuredClone` is declared locally because `packages/core`'s tsconfig has no `DOM` lib
 * (it must stay RN-safe); the global exists at runtime in every web/electron target.
 */
declare function structuredClone<T>(value: T): T;

/**
 * `helper/saleHelpers` — WEB half. Currently one symbol: `getBasicSaleData`, the factory
 * `SaleSlice` uses for its initial state and its reset/logout paths.
 *
 * ⚠️ **Genuinely divergent from mobile, in four fields** — collapsing them would change what a
 * brand-new sale looks like on one platform:
 *
 * | field | web (here) | mobile |
 * |---|---|---|
 * | `createTickets` | `false` | `true` |
 * | `createdAt` | `""` | `moment().format("DD-MM-YYYY | LTS")` |
 * | `saleBy` | `undefined` | `{}` |
 * | `paymentInfo` / `tags` | absent | `{ payments: [], balanceAmount: 0 }` / `[]` |
 *
 * **Session 24 added the SALE-PRICING closure** — `calculateAndSaveSaleData` and everything it
 * reaches. That was previously deferred with "it reads the store"; the 2026-08-21 store-access
 * decision removed that objection, so those bodies now read through `getState`/`dispatch`.
 * Still in the app: the print and facade calls, and the rest of the ~1300-line file.
 *
 * ⚠️ **This module and `helpers/updateDiscountApprovalState` import EACH OTHER** — the pricing
 * code calls `checkAndUpdateApprovalState`, which calls `validateManualDiscountLimitFn` from
 * here. That cycle exists in the apps already and survives because every participant is a
 * hoisted `function` declaration, not a const. Preserved rather than broken, so behaviour is
 * unchanged; do not convert these to arrow consts.
 */
import type {
  ChannelTypes,
  SaleDataType,
  SaleStatusTypes,
  ServiceLabel,
  TransactionType,
} from "../../models/sale";

export * from "./shared";

/* ───────────────────────── sale-pricing closure (WEB) ───────────────────────── */

/** Hand back the library's manual-discount-limit validator. */
export function validateManualDiscountLimitFn() {
  return saleutil.validateManualDiscountLimit;
}

/** The current sale from the store, deep-cloned so callers may mutate it freely. */
export function getClonedSaleDataFromReducer() {
  const saleData = store.getState().saleDataStore?.saleData || {};
  return structuredClone(saleData);
}

/**
 * Whether a bill discount survives recalculation. Identifiable entries are kept, transient
 * scratch state from an in-progress edit is dropped:
 *
 * - `discountId` — catalog discount or deal.
 * - `mid` — coupon / external-partner discount; these have no `discountId`.
 * - loyalty points — have neither id, and must stay on the bill until their refund succeeds
 *   (`docs/flows/LOYALTY_REFUND.md`).
 *
 * **Reuse it for any new retention filter** — two divergent inline literals previously stripped
 * applied loyalty points mid-edit.
 */
export function shouldRetainBillDiscount(discount: any): boolean {
  return !!(discount?.discountId || discount?.mid || isLoyaltyPointDiscountType(discount));
}

/** The catalog item records behind a sale's items, in order. */
export function getSaleItems(items: any[] = []) {
  let itemData = store.getState()?.itemsStore?.itemsData || {}, data = [];
  if (items.length) {
    for (let i = 0; i < items.length; i++)
      data.push(itemData?.[items?.[i]?.itemId]);
  }
  return data;
}

/**
 * Every discount available to the current bill: the catalog's, plus any applied coupon expanded
 * into discounts by the RL library.
 */
export function getAllDiscounts() {
  const catalogStore = store.getState().catalogStore,
    discountInfo = store.getState().discountStore.discountInfo;
  let result: any = [], discounts: any = [];
  if (!!catalogStore?.discounts && !!Object.values(catalogStore.discounts)?.length) {
    result = [...(Object.values(catalogStore.discounts))];
  }
  if (!!discountInfo?.discounts?.length) {
    let discount = saleutil.couponToDiscounts({ coupons: [discountInfo] });
    discount = discount?.length ? discount : [];
    discounts.push(...discount);
  }
  result = [...result, ...discounts];
  return result;
}

/**
 * Build the parameter bundle the RL library needs to price a sale.
 *
 * ⚠️ **`charges` MUST be cloned — the library WRITES to it** (`charge.scope`). The other catalog
 * params are read-only inside the library (filter/find only), which is why only some are cloned.
 */
export function getParamsForSaleCalculate(saleData: any) {
  const catalogStore = store.getState().catalogStore,
    businessStore = store.getState().businessStore;
  return ({
    // charge objects are mutated by charge_util (charge.scope is written) — clone required
    "charges": !!catalogStore?.charges ? structuredClone(Object.values(catalogStore.charges)) : [],
    // all other catalog params are read-only inside the library (filter/find only)
    "bizBrands": catalogStore?.bizBrandsData || [],
    "billRoundingMode": !!businessStore.businessData?.roundingMode ? businessStore.businessData.roundingMode : "",
    "branchTime": !!businessStore.businessData?.timeZone?.zoneId ? moment().tz(businessStore.businessData.timeZone.zoneId).format('YYYY-MM-DDTHH:MM:SS') : moment().format("YYYY-MM-DDTHH:MM:SS"),
    "brands": catalogStore?.brandsData || [],
    "categories": catalogStore.categoryData ? Object.values(catalogStore.categoryData) : [],
    "categoryGroups": catalogStore?.categoryGroup || [],
    "channelDefs": catalogStore.channelDef || [],
    "channelvos": catalogStore.channelvos || [],
    "discounts": structuredClone(getAllDiscounts()),
    "items": structuredClone(getSaleItems(saleData.saleItems)),
    "itemsData": structuredClone(store.getState().itemsStore?.itemsData || {}),
    "optionSetsData": structuredClone(store.getState().itemsStore?.optionMapData || {}),
    "taxArea": catalogStore.taxArea || [],
    "taxTypes": catalogStore.taxTypes || [],
    "transactionEnd": businessStore.businessData?.transactionEnd,
    "roundOnlyCashPaymentMode": businessStore?.businessData?.enableRoundingCashOnly || false
  })
}

/** Price a sale through the RL library. Thin wrapper; no store access. */
export function calculateSaleDataPrice(sale: any, payload: any = {}) {
  let data = saleutil?.calculateForSale(sale, payload);
  return data;
}

/**
 * Price a sale, building its params first.
 *
 * ⚠️ **The NC (no-charge) branch MUTATES `sale` IN PLACE and returns the same object**, where the
 * normal branch returns a NEW object from the library. Callers depend on both shapes; preserved.
 */
export function calculateSale(sale: any) {
  let params = getParamsForSaleCalculate(sale);
  if (sale.transactionType === 'NC') {
    saleutil.calculateForNC(sale, params);
    return sale;
  }
  let result = saleutil?.calculateForSale(sale, params);
  return result;
}

/** A deep copy of a sale record. */
export function getSaleRecordDataCopy(saleData: any = {}) {
  return structuredClone(saleData);
}

/**
 * Resolve one charge's amount by pricing a copy of the sale and reading the charge back out.
 *
 * ⚠️ Expensive: a full `calculateSale` per charge. That is the apps' own design; preserved.
 */
export function getAmountForCharge(origCharge: any, saleRecord: any) {
  let sale = calculateSale(getSaleRecordDataCopy(saleRecord)), amount = 0;
  if (!!sale.chargeList?.length) {
    sale.chargeList.forEach(function (charge: any) {
      if (charge.chargeId === origCharge.chargeId) {
        amount = charge.amount;
      }
    });
  }
  return amount;
}

/**
 * Recompute every charge on a sale and write the amounts back onto its `chargeList`.
 *
 * ⚠️ `chargeTotal` is accumulated and then never used — dead in both apps. Left in place: it is
 * not the only difference between the platforms, so ledger rule 2(a) does not fire.
 */
export function calculateCharges(saleData: any) {
  let saleChargeStore = structuredClone(saleData?.chargeList ?? []), chargeTotal = 0,
    saleRecord = getClonedSaleDataFromReducer();
  saleChargeStore.forEach(function (charge: any) {
    let chargeType = charge.chargeType, chargeRate = charge.chargeRate, amount = 0;
    amount = getAmountForCharge(charge, saleRecord);
    chargeTotal = chargeTotal + amount;
    charge.amount = amount;
  });
  saleData["chargeList"] = saleChargeStore;
  return saleData;
}

/**
 * Ask the loyalty partner to unblock a coupon that is being removed from the bill.
 *
 * ⚠️ Class-1 partners only. The guard returns EARLY — and shows a toast — when any required
 * field is missing, so the dispatch below never runs in that case.
 *
 * The loyalty saga reads `postBody.metaId` (with `customerRefId`) to decide whether to refetch
 * the customer's balance/milestones after the unblock succeeds — only a DotPe-provider unblock
 * triggers that extra call.
 */
export function removeExternalDiscount(
  saleData: SaleDataType,
  data: any,
) {
  const externalProviderData = getActiveProvider();
  const apppliedDiscountInfo = store.getState().discountStore.discountInfo;
  const xenoRefId = store.getState().LoyaltyStore?.externalRefId;
  const currentLoyaltyProviders = store.getState().businessStore.businessData?.loyaltyProviders || [];
  const loyaltyProviderData = currentLoyaltyProviders?.find((provider: any) => provider.metaId === data?.couponProvider);
  // in case of multiple providers, providerId will be fetched based on couponProvider of discount,
  // if not found then fallback to active provider
  const providerId = loyaltyProviderData?.mid || externalProviderData?.mid;
  const metaId = data?.couponProvider || externalProviderData?.metaId;
  const couponCode = apppliedDiscountInfo?.couponCode || data?.couponCode || data?.discountCode;
  const customerPhone = (saleData as any)?.customerInfo?.contactInfo?.phoneNumber || '';

  if (isExternalProviderC1(metaId)) {
    // Only call API if all required fields are present
    if (!xenoRefId || !providerId || !couponCode || !customerPhone) {
      if (!customerPhone) {
        notifyError("Customer phone number is missing");
      } else {
        notifyError("Something went wrong");
      }
      return;
    }
  }

  const payload = {
    postBody: {
      referenceId: xenoRefId,
      providerId,
      metaId,
      couponCode,
      customerPhone,
      sale: saleData
    },
    customerRefId: (saleData as any)?.customerInfo?.customerRefId,
    callback: (data: any) => {
      console.log(data);
    }
  }
  store.dispatch(unBlockExternalDiscount(payload));
}

/**
 * Remove one discount from a cloned sale and return the clone.
 *
 * ⚠️ **An AUTO-mode discount is not deleted — its `priority` is set to `-1`** so the library
 * re-evaluates it as unapplied. Only manual/coupon entries are spliced out.
 *
 * ⚠️ Both lookups accept `discount.mid` as well as `discount.discountId`, because a caller may
 * hand this either a BILL entry or the raw catalog OFFER it came from — `buildBillDiscount`
 * writes `discountId: offer.mid || offer.discountId`, so the two are the same id under two names.
 * `packages/core/discounts`' `removeOffer` always passes the bill entry; the POS's own call sites
 * are less consistent, and a miss here silently leaves the discount on the bill.
 */
export function removeDiscountFromCart(
  saleData: SaleDataType,
  discount: any,
) {
  const billDiscountListData = (saleData as any)?.billDiscountList || [];
  const activeProvider = getActiveProvider();
  const matchesDiscount = (discountItem: any) =>
    discountItem.discountId === discount.discountId || discountItem.discountId === discount.mid;
  const discountListData = billDiscountListData?.find(matchesDiscount);
  const externalProviderID = discountListData?.couponProvider || activeProvider?.metaId;
  const isLoyaltyBoundDiscount = !!discount?.isExternal || isLoyaltyPointDiscountType(discount) || externalProviderID === EXTERNAL_LOYALTY_PARTNERS.DOTPE;
  if (isExternalLoyaltyProvider(externalProviderID) || isLoyaltyBoundDiscount) {
    removeExternalDiscount(saleData, discount)
  }
  let tempSaleData: any = structuredClone(saleData),
    billDiscountList = tempSaleData.billDiscountList;
  if (!!billDiscountList?.length) {
    let index = billDiscountList.findIndex(matchesDiscount);
    if (index > -1) {
      if (billDiscountList?.[index]?.mode === DISCOUNT_MODES.auto) {
        billDiscountList[index]["priority"] = -1;
      } else {
        billDiscountList.splice(index, 1);
      }
      tempSaleData.billDiscountList = billDiscountList;
    }
  }
  if (discount.discountType === DISCOUNT_TYPES.voucher || isExternalProviderC1(discount.couponProvider) || discount.couponCode) {
    store.dispatch(applyDiscountSuccess({}));
  }
  return tempSaleData;
}

/**
 * Drop coupons that were on the previous bill but did not survive this recalculation.
 *
 * Auto-mode discounts are exempt — the library manages those itself.
 */
function removeUnappliedCoupons(sale: SaleDataType, prevSale: SaleDataType) {
  let clonedSale: any = structuredClone(sale) || {};
  const clonedPrevSale: any = structuredClone(prevSale) || {};

  if (!clonedSale?.discountList) {
    clonedSale.discountList = [];
  }
  if (!clonedPrevSale?.discountList) {
    clonedPrevSale.discountList = [];
  }

  const discountIds = clonedSale.discountList.map((discount: any) => discount.discountId);
  const prevDiscountIds = clonedPrevSale?.discountList?.map((discount: any) => discount.discountId);

  const discountIdsSet = new Set(discountIds);
  const prevDiscountIdsSet = new Set(prevDiscountIds);

  const billDiscountList = clonedSale?.billDiscountList || [];

  billDiscountList.forEach((discount: any) => {
    if (discount.mode !== DISCOUNT_MODES.auto && !discountIdsSet.has(discount.discountId) && prevDiscountIdsSet.has(discount.discountId)) {
      clonedSale = removeDiscountFromCart(clonedSale, discount);
    }
  })
  return clonedSale;
}

/**
 * Price a sale and save it. The entry point the `sale` saga calls.
 *
 * 🔴 **Divergent from the other platform on FIVE axes** — see `index.native.ts`:
 *  1. a `skipSave` parameter, so callers can price without dispatching;
 *  2. bill discounts are filtered through `shouldRetainBillDiscount` — unless the sale came from
 *     the API, in which case the list is passed through untouched;
 *  3. `customerRefId` is backfilled from `customerInfo.mid` before discounts are computed;
 *  4. unapplied coupons are swept afterwards (again, skipped for API-sourced sales);
 *  5. it clones with `structuredClone` and does NOT re-serialise the params.
 */
export function calculateAndSaveSaleData(
  data: any,
  skipSave: boolean = false,
  dryRun: boolean = false,
) {
  const prevSaleData = store.getState().saleDataStore.saleData || {};
  let saleData = structuredClone(data), params = getParamsForSaleCalculate(saleData);

  let filteredDiscount = saleData.sourceInfo?.source === SALE_SOURCE_TYPES.API ?
    saleData?.billDiscountList :
    saleData?.billDiscountList?.filter(shouldRetainBillDiscount);
  saleData = { ...saleData, billDiscountList: filteredDiscount };

  saleData = calculateCharges(saleData);
  // Ensure customerRefId is present for discount calculations
  if (saleData?.customerInfo && !saleData?.customerInfo?.customerRefId && saleData?.customerInfo?.mid) {
    saleData.customerInfo.customerRefId = saleData.customerInfo.mid;
  }
  let sale = calculateSaleDataPrice(saleData, params);

  if (sale.sourceInfo?.source !== SALE_SOURCE_TYPES.API) {
    sale = removeUnappliedCoupons(sale, prevSaleData);
  }
  !skipSave && store.dispatch(saveSaleData(sale));
  if (
    !dryRun &&
    Math.abs(sale?.discountTotal || 0) < Math.abs(prevSaleData?.discountTotal || 0)
  ) { //check if any discount got removed
    checkAndUpdateApprovalState(sale);
  }
  return sale;
}

/**
 * Reset the working sale to a blank one, carrying the selected channel over.
 *
 * ⚠️ **Divergent: this platform also clears the persisted previous-sale key** from local storage.
 * The other platform does not — see `index.native.ts`.
 */
/**
 * ⚠️ **This is where a kiosk sale gets `channel: "KIOSK"`, and it is deliberately not
 * `getBasicSaleData`.**
 *
 * `getBasicSaleData` is called at module-EVAL time by `SaleSlice`'s `initialState`, and that
 * file's header records the boot-time `TypeError` that cost the apps twice when that call
 * reached across modules. Reading the kiosk flag there would put `kiosk/config` — and the
 * store it reads — back on that eval path. This helper runs only when something actually
 * resets a sale, so the same read is free of that hazard.
 *
 * Coverage is total despite being one function: every kiosk path routes through it. Entering
 * and leaving kiosk mode, the idle timeout, the "start over" button and finishing an order all
 * call `kiosk/session`'s `stateTeardownSteps().resetSale`, which is this. The POS calls it too,
 * which is exactly why the stamp is a branch on the mode rather than a constant.
 */
export function resetSaleData() {
  let saleData: any = getBasicSaleData();
  /**
   * TODO(kiosk-channel): stamp `CHANNEL_TYPES.KIOSK` here once the backend accepts it.
   *
   * HELD BACK ON PURPOSE — this is not an oversight. `KIOSK` is a value this client has never
   * sent: it appears nowhere in `@dotpe/rista-library` or `@dotpe/rista-pos-common`, and if the
   * sale API validates `channel` against a fixed enum, every kiosk sale would fail to save.
   * Until that ships, a kiosk sale stays on `POS` exactly as before.
   *
   * Everything else for the switch is already in place, so turning it on is this block alone:
   *
   *     if (isKioskModeEnabled()) {
   *       saleData.channel = CHANNEL_TYPES.KIOSK;
   *     }
   *
   * `CHANNEL_TYPES.KIOSK` exists, `ChannelTypes` admits it, and `isPosChannel` — which all 77
   * of the apps' POS-channel checks now go through — already answers true for it. So the
   * moment this block is uncommented, kiosk orders behave like POS orders everywhere without
   * another edit.
   *
   * ⚠️ One thing to fix at the same time: `runModeTransition("enter")` runs this teardown
   * BEFORE `setMode()` (deliberately — see `kiosk/mode/modeModel`), so `isKioskModeEnabled()`
   * is still false here on the mode-entry path. The first customer after staff enable kiosk
   * mode would be stamped `POS`. Stamp at `kiosk/session`'s `beginSession()` as well, which is
   * the "a customer is starting a new order" moment and always runs with the flag on.
   */
  let selectedChannelData = store.getState().saleDataStore?.selectedChannelData;
  if (selectedChannelData?.channelDefId) { // adding channel data in sale
    saleData["channelId"] = selectedChannelData?.channelId;
    saleData["channelDefId"] = selectedChannelData?.channelDefId;
    saleData["groupId"] = selectedChannelData?.groupId;
  }
  resetCustomer();
  removeLocalStorage(LOCALSTORAGE_KEYS.PREVIOUS_SALE_DATA);
  store.dispatch(saveSaleData(saleData));
  store.dispatch(updateCurrentTableInfo({}));
  store.dispatch(applyDiscountSuccess({}));
}

/**
 * The current sale from the store.
 *
 * 🔴 **Divergent from the other platform in BOTH respects, and the first one is a live hazard:**
 * this version returns the store object **UNCLONED**, so a caller that mutates the result mutates
 * redux state in place. The other platform deep-clones. It also falls back to `{}` where the
 * other returns a fully-formed blank sale.
 *
 * Preserved exactly — cloning here would change the aliasing every existing web caller relies on
 * (and `getClonedSaleDataFromReducer`, right above, is the cloning variant callers reach for when
 * they need one). Flagged rather than fixed.
 */
export function getSaleDataFromReducer() {
  const saleData = store.getState().saleDataStore?.saleData || {};
  return saleData;
}

/**
 * Roll a sale up into the counts the analytics funnel reports.
 *
 * ⚠️ **Per-variant, and the divergence is in the RETURN SHAPE**: this platform additionally
 * counts `recommendedUniqueItemsCount` and `catalogUniqueItemsCount` (distinct line items, not
 * quantities), which the other platform's copy does not compute or return. A shared body would
 * either add two fields to that platform's analytics payload or drop two from this one.
 */
export function summarizeSaleData(saleData: any) {
  let recommendedItemsCount = 0;
  let catalogItemsCount = 0;
  let recommendedItemsTotal = 0;
  let catalogItemsTotal = 0;
  let totalItemsCount = 0;
  let recommendedUniqueItemsCount = 0;
  let catalogUniqueItemsCount = 0;

  for (const item of saleData.saleItems) {
    totalItemsCount += item.quantity;

    if (item.recommendationInfo) {
      recommendedItemsCount += item.quantity;
      recommendedItemsTotal += item.itemSubTotal;
      recommendedUniqueItemsCount += 1;
    } else {
      catalogItemsCount += item.quantity;
      catalogItemsTotal += item.itemSubTotal;
      catalogUniqueItemsCount += 1;
    }
  }
  return {
    recommendedItemsCount,
    catalogItemsCount,
    recommendedItemsTotal,
    catalogItemsTotal,
    billTotal: saleData.billTotal,
    totalItemsCount,
    recommendedUniqueItemsCount,
    catalogUniqueItemsCount
  };
}

/**
 * Hand the sale to the rista-library's `saleutil.populateMetaInfo`, with this terminal's header,
 * audit, POS-settings and channel context attached.
 *
 * 🔴 **PER-VARIANT, and the divergence is real on three counts** (diffed token-by-token against
 * the other platform):
 *   1. every clone here is `structuredClone`, the other side uses lodash `cloneDeep` — which is
 *      NOT the same function (dates, `undefined`, Maps all survive differently), so this is not a
 *      formatting difference;
 *   2. `bizBrands` is guarded with `!!…?.length`, the other side with a bare `…?.length`;
 *   3. the timestamp is `moment().tz(...)`, the other side calls the imported `tz(...)`.
 *
 * ✅ A FOURTH count is RETIRED: the two platforms once reached the library by different routes
 * (a namespaced browser global here, a bare global there). Both now import the same
 * `sale_util.js` module from the package, so that axis no longer diverges at all.
 *
 * ⚠️ The library call is wrapped in `try/catch` that only LOGS: on failure the sale comes back
 * with header/audit unpopulated rather than throwing. Preserved exactly — though with a static
 * import the "library absent" case it was written for can no longer happen.
 *
 * ⚠️ Reads state through core's own store at CALL time, where the app file used its `store`
 * singleton.
 */
export function populateMetaInfo(saleData: any) {
  let headerInfo: any = {},
    auditInfo: any = {};

  const businessStore = store.getState().businessStore,
    businessDataFromStore = businessStore?.businessData,
    userData = businessStore?.userData,
    posPreferences = structuredClone(businessStore?.businessData.posPreferences) || {},
    posSettings = structuredClone(store.getState().catalogStore?.posSettings),
    channelDefId = store.getState().saleDataStore?.selectedChannelData?.channelDefId,
    channelDef = getChannelDef(channelDefId),
    bizBrands = !!store.getState().catalogStore?.bizBrandsData?.length ? store.getState().catalogStore.bizBrandsData : [];

  const businessData = structuredClone(businessDataFromStore);

  headerInfo.branchAddress = businessData.branchAddress;
  headerInfo.branchName = businessData.branchName;
  headerInfo.businessName = businessData.businessName;
  headerInfo.businessType = businessData.businessType;
  headerInfo.billTemplate = businessData.billTemplate;
  headerInfo.contactInfo = businessData.contactInfo;
  headerInfo.currency = businessData.currencyCode;
  headerInfo.branchCode = businessData.branchCode;
  headerInfo.businessHours = businessData.businessHours;
  headerInfo.taxId = businessData.taxId;
  headerInfo.taxStateCode = businessData.taxStateCode;

  auditInfo.date = moment().tz(businessData?.timeZone?.zoneId || "Asia/Calcutta").format();
  auditInfo.buid = userData.businessUserId;
  auditInfo.userName = userData.name;
  auditInfo.userId = userData.userId;
  auditInfo.transactionEnd = businessData.transactionEnd;
  auditInfo.deviceIdentifier = getLocalStorage("deviceIdentifier");
  auditInfo.deviceLabel = businessData.deviceInfo.label;

  saleData = structuredClone(saleData);
  try {
    saleutil.populateMetaInfo({
      sale: saleData,
      headerInfo: headerInfo,
      audit: auditInfo,
      posSettings: posSettings,
      posPreferences: posPreferences,
      channelDef: channelDef,
      bizBrands: bizBrands
    });
  } catch (error) {
    console.log("populateMetaInfo", error);
  }
  //Transient fields to be used at the time of printing only.
  delete saleData.opType;
  delete saleData.printNote;
  return saleData;
}

/**
 * Ask the rista-library whether this sale should create kitchen tickets for the given `opType`.
 *
 * 🔴 **PER-VARIANT.** This side clones the sale out of the reducer and carries TWO guards the
 * other platform does not have at all: advance orders whose channel
 * delays ticket creation, and adhoc service labels whose channel skips it. Both return `false`
 * before the library is consulted.
 */
export function checkCreateTicket(opType: string = CREATE_TICKET_ON_SAVE) {
  let saleData = getClonedSaleDataFromReducer(),
    posSettings = !!store.getState()?.catalogStore?.posSettings ? store.getState().catalogStore.posSettings : {};
  let channelDef = getChannelDef(saleData.channelDefId);
  if (isAdvanceOrder(saleData) && channelDef?.delayAdvanceOrderTicketCreation)
    return false; // do not create ticket in case of advance order
  if (saleData?.serviceLabel?.isAdhoc && channelDef.skipAdhocTicketCreation) return false;
  let result = saleutil.checkTicketCreation({
    sale: saleData,
    posPreferences: posSettings,
    posSettings: posSettings, opType: opType,
    channelDef
  });
  return result;
}
